import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ContactForm from "./ContactForm";
import { Helmet } from 'react-helmet';
import { useNavigate, useLocation } from 'react-router-dom';
 
export default function ArogyaNarayan() {
  const location = useLocation();
  const navigate = useNavigate();
 
  const SCROLL_SHOW_THRESHOLD = 300;
 
  const [showScroll, setShowScroll] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
 
  const scrollTimeoutRef = useRef(null);
 
  const RADIUS = 49;
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
  const dashOffset = CIRCUMFERENCE - (scrollProgress / 100) * CIRCUMFERENCE;
 
  // ────────────────────────────────────────────────
  // Aggressive scroll-to-top on every load / refresh / route change
  // ────────────────────────────────────────────────
  useLayoutEffect(() => {
    // Prevent browser from restoring scroll position automatically
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
 
    const forceScrollTop = () => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };
 
    // Run multiple times with increasing delay to override late browser restore
    forceScrollTop();
    requestAnimationFrame(forceScrollTop);
 
    const t1 = setTimeout(forceScrollTop, 0);
    const t2 = setTimeout(forceScrollTop, 50);
    const t3 = setTimeout(forceScrollTop, 150);
    const t4 = setTimeout(forceScrollTop, 300);
 
    // Extra safety on page events that can restore scroll
    const handlePageShow = (e) => {
      if (e.persisted) forceScrollTop(); // bfcache / back-forward cache
      forceScrollTop();
    };
 
    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('load', forceScrollTop, { once: true });
 
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('load', forceScrollTop);
    };
  }, [location.pathname]);
 
  const onScroll = useCallback(() => {
    const sc = window.scrollY || window.pageYOffset;
    setShowScroll(sc > SCROLL_SHOW_THRESHOLD);
 
    const doc = document.documentElement;
    const totalScroll = Math.max(0, doc.scrollHeight - doc.clientHeight);
    const progress = totalScroll > 0 ? (sc / totalScroll) * 100 : 0;
 
    setScrollProgress(Math.min(100, Math.max(0, progress)));
    setIsScrolling(true);
 
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
 
    scrollTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
      scrollTimeoutRef.current = null;
    }, 250);
  }, []);
 
  useEffect(() => {
    // Initial call after mount
    onScroll();
 
    window.addEventListener("scroll", onScroll, { passive: true });
 
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, [onScroll]);
 
  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);
 
  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      scrollToTop();
    }
  };
 
  return (
    <div className="w-full mt-12 pt-10 overflow-hidden font-sans text-[#063852]">
      <Helmet>
        <title>Hospital Management System | AI-Powered HMS | SecurXpert</title>
        <meta
          name="description"
          content="AI-enabled Hospital Management System by SecurXpert to automate clinical workflows, EMRs, billing, compliance and patient care."
        />
      </Helmet>
 
      <div className="fixed top-0 left-0 w-full z-[9999]">
        <Navbar />
      </div>
 
      {/* ================= HERO SECTION ================= */}
      <section className="relative w-full bg-white px-6 mb-16">
        <div className="absolute top-0 right-0 w-[420px] h-[120px] bg-[#cae9ff] rounded-bl-full opacity-70 -z-10"></div>
 
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <img
              src="/img/52.png"
              alt="AI powered hospital management system dashboard"
              className="w-24 mb-6"
            />
            <p className="text-sm tracking-wide font-semibold text-[#2c75ff] mb-2">
              ALL-IN-ONE HEALTHCARE PLATFORM
            </p>
 
            <h1 className="text-3xl md:text-4xl font-bold leading-tight mb-[38px]">
              Best Hospital Management System for Healthcare
            </h1>
 
            <p className="text-gray-600 mt-4 leading-relaxed mb-8">
              Arogya Narayan is a powerful all-in-one healthcare platform designed to simplify hospital and clinic management through intelligent digital solutions. It integrates patient management, appointment scheduling, billing, electronic medical records and reporting into a single unified system. The platform helps healthcare organizations improve operational efficiency, reduce administrative workload and enhance patient satisfaction. With AI-enabled insights and secure cloud infrastructure,
              
                Arogya Narayan
              
              ensures data accuracy, compliance, and scalability. It empowers healthcare providers to deliver faster, smarter and more patient-centric care in today's digital healthcare ecosystem.
            </p>
          </div>
 
          <div className="mt-5">
            <img
              src="/img/hmshero.jpg"
              alt="MocDoc HMS demo showing hospital workflows and patient management"
              className="w-full drop-shadow-lg"
            />
          </div>
        </div>
      </section>
 
      {/* ================= VIDEO SECTION ================= */}
      <section className="w-full py-4 px-6 md:px-0 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mx-auto mb-10 text-center">
            <h2 className="text-3xl font-bold text-[#063852]">
              Watch Arogya Narayan HMS in Action
            </h2>
            <p className="text-gray-600 mt-3">
              See how Arogya Narayan HMS makes hospital management simple and efficient. Automate
              workflows, improve patient care and keep everything running smoothly with our
              AI-powered platform.
            </p>
          </div>
 
         <div className="relative rounded-2xl md:rounded-3xl overflow-hidden shadow-2xl">
  <video
    className="w-full h-auto max-h-[240px] sm:max-h-[360px] md:max-h-none block"
    src="/video/Arogya Narayan.mp4"
    muted
    playsInline
    autoPlay
    loop
    preload="auto"
    controls
  >
    Your browser does not support HTML5 video.
  </video>
</div>

        </div>
      </section>
 
      {/* ================= WHY CHOOSE US ================= */}
      <section className="w-full py-20 px-6 md:px-16 bg-[#f8fbff]">
        <h2 className="text-3xl text-center font-bold mb-4">
          Why Choose Our Arogya Narayan for Digital Transformation?
        </h2>
        <p className="text-center text-gray-600 mb-12">
          Transforming Healthcare – Smarter & Faster
        </p>
 
        <div className="max-w-7xl mx-auto grid md:grid-cols-4 gap-10">
          <div className="bg-white p-6 shadow rounded-xl text-center">
            <h3 className="font-bold text-lg mb-2">1. End-to-End Digital Upgrade</h3>
            <p className="text-gray-600 text-sm">
              Upgrade your healthcare facility into a smart, automated and data-driven digital environment with next-gen Hospital Management solutions.
            </p>
          </div>
 
          <div className="bg-white p-6 shadow rounded-xl text-center">
            <h3 className="font-bold text-lg mb-2">2. Modern & User-Focused Interface</h3>
            <p className="text-gray-600 text-sm">
              A smart, responsive HMS interface built to boost staff productivity, streamline daily operations and improve overall workflow efficiency.
            </p>
          </div>
 
          <div className="bg-white p-6 shadow rounded-xl text-center">
            <h3 className="font-bold text-lg mb-2">3. Mobile-Ready Hospital Operations</h3>
            <p className="text-gray-600 text-sm">
              Access EMRs, billing, appointments and patient updates anytime, anywhere— ensuring uninterrupted operations across devices.
            </p>
          </div>
 
          <div className="bg-white p-6 shadow rounded-xl text-center">
            <h3 className="font-bold text-lg mb-2">4. Digital Healthcare Architecture</h3>
            <p className="text-gray-600 text-sm">
              Built to grow with you from single clinics to multi-hospital networks with seamless scalability and modular expansion.
            </p>
          </div>
        </div>
      </section>
 
      {/* ================= DIGITAL TRANSFORMATION ================= */}
      <section className="w-full py-20 px-6 md:px-16 relative">
        <div className="absolute left-0 top-20 w-[280px] h-[280px] bg-[#d9efff] rounded-r-full opacity-40 -z-10"></div>
 
        <h2 className="text-3xl font-bold mb-4">Digital Transformation Capabilities</h2>
        <p className="text-gray-600 mb-4 max-w-2xl">
          Smart, End-to-End Hospital Digital Solutions
        </p>
 
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-10 items-center">
          <ul className="space-y-4 text-gray-700">
            <li>
              <span className="font-semibold">• Automated Operational Workflows</span><br />
              Digitise administrative, clinical and financial processes with smart automation to cut manual work and gaps.
            </li>
            <li>
              <span className="font-semibold">• Complete Patient Pathway Management</span><br />
              Manage the full patient journey Registration → Consultation → EMR → Diagnostics → Treatment → Billing → Discharge on one platform.
            </li>
            <li>
              <span className="font-semibold">• Integrated Hospital Operations Suite</span><br />
              Comprehensive modules: Pharmacy, Lab, Radiology, Bed & Ward, Inventory, HR, Finance and more.
            </li>
            <li>
              <span className="font-semibold">• Regulatory & Compliance Built-In</span><br />
              Ensure NABH, HIPAA and other healthcare compliance with integrated, audit-ready reporting.
            </li>
          </ul>
 
          <img
            src="/img/hms1.jpg"
            className="w-full drop-shadow-lg"
            alt="Digital Capability"
          />
        </div>
      </section>
 
      {/* ================= KEY FEATURES ================= */}
      <section className="w-full py-20 px-6 md:px-16 bg-[#f8fbff]">
        <h2 className="text-3xl font-bold text-center mb-4">
          Key Features of Our Digital Healthcare
        </h2>
        <p className="text-center text-gray-600 mb-12 max-w-3xl mx-auto">
          Next-gen intelligent, secure, and automated features to modernise workflows and enable a digital healthcare experience.
        </p>
 
        <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-8">
          {[
            {
              title: "1. Personalised User Dashboards",
              text: "Role-based access ensures doctors, nurses, lab teams and administrators view only the tools and information they need.",
            },
            {
              title: "2. Seamless Dashboards",
              text: "Visual dashboards and flexible forms, along with approvals and processes, are tailored to match your hospital's operational structure.",
            },
            {
              title: "3. AI-Driven Scheduling & Planning",
              text: "AI intelligently optimises bed management, OT utilisation, and overall resource allocation, enhancing patient flow while reducing operational downtime effectively.",
            },
            {
              title: "4. Offline Operational Continuity",
              text: "Maintain uninterrupted hospital workflows even when the network is down, with auto-sync once connectivity returns.",
            },
            {
              title: "5. Seamless System Interoperability",
              text: "Effortless integration with EHR, PACS/RIS/LIS, payment systems, and national health portals, plus voice-enabled documentation for faster notes.",
            },
            {
              title: "6. Patient Engagement & Automation",
              text: "Automated WhatsApp and SMS notifications, instant NABH/NABL reporting and built-in compliance tools enhance patient communication and accreditation readiness.",
            },
          ].map((feature, i) => (
            <div
              key={i}
              className="group bg-white p-6 rounded-xl shadow border border-transparent relative overflow-hidden transition-all duration-300 hover:shadow-xl hover:scale-[1.03]"
            >
              <span className="absolute top-0 left-0 w-0 h-[3px] bg-[#2c75ff] transition-all duration-300 group-hover:w-full"></span>
              <span className="absolute bottom-0 right-0 w-0 h-[3px] bg-[#2c75ff] transition-all duration-300 group-hover:w-full"></span>
 
              <h3 className="font-bold text-lg mb-2">{feature.title}</h3>
              <p className="text-gray-600 text-sm">{feature.text}</p>
            </div>
          ))}
        </div>
      </section>
 
      {/* Contact section */}
      <section id="contact" className="py-16 bg-gradient-to-b from-white to-gray-900">
        <div className="max-w-4xl mx-auto px-6">
          <h3 className="text-3xl md:text-4xl font-extrabold text-center mb-6">
            Get in touch
          </h3>
          <p className="text-center text-black/70 mb-8">
            Have a question, need pricing, or want a customised HMS demo for your hospital? Share your
            details and our team will connect with you.
          </p>
 
          <ContactForm />
        </div>
      </section>
 
      <Footer />
 
      {/* Scroll to Top Button */}
      {showScroll && (
        <div
          onClick={scrollToTop}
          onKeyDown={handleKeyDown}
          role="button"
          tabIndex={0}
          className="scroll-top fixed bottom-6 right-6 z-50 cursor-pointer flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-r from-blue-500 to-blue-700 shadow-xl hover:scale-110 transition-all"
        >
          <svg className="progress-circle -rotate-90 w-12 h-12 absolute" viewBox="0 0 100 100">
            <circle cx="50" cy="50" r={RADIUS} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="4" />
            <circle
              cx="50"
              cy="50"
              r={RADIUS}
              fill="none"
              stroke="white"
              strokeWidth="4"
              strokeDasharray={CIRCUMFERENCE}
              strokeDashoffset={dashOffset}
              strokeLinecap="round"
            />
          </svg>
 
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-6 h-6 text-white relative z-10"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
          </svg>
        </div>
      )}
    </div>
  );
}
 